import {
  Box,
  Button,
  Chip,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  downloadFileBlobStorageIngestDownloadDatasetIdGet,
  useGetVersionHistoryIngestGet,
} from "orval/generated/endpoint";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "components/common/Notification/showSnackbar";
import { IngestViewModel } from "orval/generated/models";
import getFormattedDateForDownloads from "../../../../utils/getFormattedDateForDownloads";
import { useGlobalLoader } from "components/common/GlobalLoader/GlobalLoader";
import { CustomIcon } from "components/common/CustomIcon/CustomIcon";
import { Icons } from "components/common/CustomIcon/types";
import downloadBlob from "../../../../utils/downloadBlob/downloadBlob";
import { getStepDisplayName } from "../../../../utils/ingestStepUtils";
import { getIngestStepProgress } from "../../../../utils/ingestStepProgress";
import { StepProgress } from "../../../../components/common/StepProgress/StepProgress";
import { CircularProgress } from "@mui/material";

interface VersionHistoryProps {
  onSwitchToUpload?: () => void;
}

function VersionHistory({ onSwitchToUpload }: VersionHistoryProps) {
  const { t } = useTranslation("productConfiguration");
  const { palette } = useTheme();
  const { showGlobalLoader } = useGlobalLoader();

  const showSnackbar = useSnackbar();

  const { data: ingestData, isLoading: isIngestDataLoading, refetch } =
    useGetVersionHistoryIngestGet();

  const rowRefs = useRef<HTMLDivElement[]>([]);
  const chipRefs = useRef<HTMLDivElement[]>([]);
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  const [chipBoxRequiredWidth, setChipBoxRequiredWidth] = useState<number>(0);
  const [countdown, setCountdown] = useState<number>(10);
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());

  const toggleErrorExpansion = (versionId: number) => {
    setExpandedErrors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(versionId)) {
        newSet.delete(versionId);
      } else {
        newSet.add(versionId);
      }
      return newSet;
    });
  };


  const updateRowHeights = () => {
    setRowHeights(
      // calculate the heights of each row in the version history
      // this will be used to accurately draw the lines between the respective chips
      // the height between chips is equal to the height of the div including the margin minus the chip height
      rowRefs.current.map((ref, index) => {
        const style = window.getComputedStyle(ref);

        const topMarginHeight = style.getPropertyValue("margin-top");
        const divHeight: number | undefined = ref?.clientHeight;
        const chipHeight = chipRefs.current[index]?.clientHeight;

        const rowHeightDiff: number =
          divHeight + parseInt(topMarginHeight, 10) - chipHeight;

        return rowHeightDiff ?? 0;
      }),
    );
  };

  const handleDownload = async (version: IngestViewModel) => {
    try {
      showGlobalLoader(true);
      const response: any =
        await downloadFileBlobStorageIngestDownloadDatasetIdGet(
          version.id,
          {
            responseType: "blob",
          },
        );

      const fileName = `PEF-${version.version}-${getFormattedDateForDownloads()}.xlsx`;

      downloadBlob(response as unknown as BlobPart, fileName);

      showSnackbar(
        t("dataManagementPage.versionHistory.downloadSuccess"),
        "success",
      );
    } catch (error) {
      showSnackbar(
        t("dataManagementPage.versionHistory.downloadError"),
        "error",
      );
    } finally {
      showGlobalLoader(false);
    }
  };

  // recalculate the row heights when window resizes
  useEffect(() => {
    window.addEventListener("resize", updateRowHeights);

    return () => {
      window.removeEventListener("resize", updateRowHeights);
    };
  }, []);

  useEffect(() => {
    updateRowHeights();
  }, [ingestData]);

  // Recalculate row heights when error expansion state changes
  useEffect(() => {
    updateRowHeights();
  }, [expandedErrors]);

  useEffect(() => {
    if (chipRefs.current.length > 0) {
      const maxWidth = Math.max(
        ...chipRefs.current.map((chip) => chip?.clientWidth || 0),
      );
      setChipBoxRequiredWidth(maxWidth);
    }
  }, [ingestData]);

  // Poll for updates when there are running ingests with countdown
  useEffect(() => {
    const hasRunningIngests = ingestData?.some(
      (version) => version.status === "running"
    );

    if (hasRunningIngests) {
      // Countdown timer
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return 5; // Reset to 5
          }
          return prev - 1;
        });
      }, 1000); // Update every second

      // Polling timer
      const pollInterval = setInterval(() => {
        refetch();
        setCountdown(5); // Reset countdown when we refetch
      }, 5000); // Poll every 5 seconds

      return () => {
        clearInterval(countdownInterval);
        clearInterval(pollInterval);
      };
    } else {
      setCountdown(5); // Reset countdown when no running ingests
    }
  }, [ingestData, refetch]);

  return (
    <Box pb={8}>
      {isIngestDataLoading
        ? Array.from({ length: 5 }, (_, index) => index).map((item, index) => (
            <Grid
              container
              mt={index === 0 ? 4 : 8}
              spacing={8}
              key={item}
              alignItems="center"
              data-testid={`version-history-row`}
            >
              <Grid>
                <Skeleton variant="circular" height={40} width={40} />
              </Grid>
              <Grid>
                <Typography variant="h4">
                  <Skeleton variant="text" height={20} width={100} />
                </Typography>
                <Typography variant="h6">
                  <Skeleton variant="text" height={15} width={100} />
                </Typography>
                <Typography variant="body1">
                  <Skeleton variant="text" height={10} width={300} />
                </Typography>
              </Grid>
            </Grid>
          ))
        : ingestData && ingestData.length > 0 
        ? ingestData.map((version, index) => {
            let chipColour: string;
            if (version.status === "running") {
              chipColour = "#1976d2"; // Blue for running
            } else if (version.status === "failed") {
              chipColour = "#d32f2f"; // Red for failed
            } else if (index === 0) {
              chipColour = "#d04a02"; // Orange for latest succeeded
            } else if (version.version.endsWith(".0")) {
              chipColour = "";
            } else {
              chipColour = "transparent";
            }

            return (
              <Grid
                container
                mt={4}
                key={version.id}
                justifyContent="space-between"
                alignItems="center"
                ref={(el: HTMLDivElement) => (rowRefs.current[index] = el)}
                data-testid={`version-history-row`}
              >
                <Grid
                  alignSelf="flex-start"
                  position="relative"
                  display="flex"
                  justifyContent="center"
                  minWidth={chipBoxRequiredWidth}
                >
                  {index !== 0 && (
                    // Creates a line above each chip component that is not the first index
                    // that uses the height difference between the two chips
                    <Box
                      sx={{
                        position: "absolute",
                        top: `-${rowHeights[index - 1] || 0}px`,
                        left: "50%",
                        width: "1px",
                        height: `${rowHeights[index - 1] || 0}px`,
                        backgroundColor: palette.textColor.light,
                      }}
                    />
                  )}
                  {version.status === "running" ? (
                    <Box
                      sx={{
                        width: "40px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "25px",
                        backgroundColor: "#1976d2",
                      }}
                      ref={(el: HTMLDivElement) => (chipRefs.current[index] = el)}
                    >
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    </Box>
                  ) : version.status === "failed" ? (
                    <Box
                      sx={{
                        width: "40px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "25px",
                        backgroundColor: "#d32f2f",
                      }}
                      ref={(el: HTMLDivElement) => (chipRefs.current[index] = el)}
                    >
                      <CustomIcon
                        name={Icons.CLOSE}
                        width="16px"
                        height="16px"
                        fill="white"
                      />
                    </Box>
                  ) : (
                    <Chip
                      label={version.version}
                      sx={{
                        borderRadius: "25px !important",
                        backgroundColor: `${chipColour} !important`,
                        color: index === 0 ? "white" : "",
                      }}
                      ref={(el: HTMLDivElement) => (chipRefs.current[index] = el)}
                      data-testid={`version-history-chip-${version.version.replace(/\./g, "")}`}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 8, md: 9 }}>
                  <Typography
                    mb={1}
                    className="header-H4"
                    fontWeight="bold"
                    tabIndex={0}
                  >
                    {version.title}
                  </Typography>
                  <Typography
                    fontSize="0.8rem"
                    sx={{ color: (theme) => theme.palette.textColor.dark }}
                    tabIndex={0}
                  >
                    {version.uploaded_by} &bull; {version.started_on}
                    {version.status === "running" && version.current_step && (
                      <span style={{ color: "#1976d2", fontWeight: "bold" }}>
                        {" "}&bull; Refreshing in {countdown}s
                      </span>
                    )}
                    {version.status === "failed" && (
                      <span style={{ color: "#d32f2f", fontWeight: "bold" }}>
                        {" "}&bull; Failed
                      </span>
                    )}
                  </Typography>
                  <Typography tabIndex={0}>{version.description}</Typography>
                  
                  {version.ingest_type && (
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <StepProgress 
                        steps={getIngestStepProgress(
                          version.ingest_type as 'full' | 'numerical',
                          version.current_step || null,
                          version.status as 'running' | 'succeeded' | 'failed'
                        )}
                        compact={true}
                        showNames={true}
                        showProgress={true}
                      />
                    </Box>
                  )}
                  
                  {version.status === "failed" && version.error && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => toggleErrorExpansion(version.id)}
                        sx={{ 
                          color: "#d32f2f", 
                          fontSize: "0.75rem", 
                          px: 1, 
                          py: 0.25,
                          minWidth: "auto",
                          textTransform: "none",
                          mb: expandedErrors.has(version.id) ? 1 : 0,
                          backgroundColor: "rgba(211, 47, 47, 0.08)",
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.12)"
                          },
                          "&:focus": {
                            backgroundColor: "rgba(211, 47, 47, 0.16)"
                          }
                        }}
                      >
                        {expandedErrors.has(version.id) ? "Show less" : "Show more"}
                      </Button>
                      {expandedErrors.has(version.id) && (
                        <Typography 
                          fontSize="0.8rem" 
                          sx={{ color: "#d32f2f", fontStyle: "italic", mt: 1 }}
                          tabIndex={0}
                        >
                          Error: {version.error}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid
                  size={{ xs: 1, md: 2 }}
                  display="flex"
                  justifyContent="flex-end"
                >
                  <Tooltip
                    title={
                      version.status === "succeeded"
                        ? t("dataManagementPage.versionHistory.downloadTooltip")
                        : version.status === "running"
                        ? "Ingest is still running"
                        : "Download not available for failed ingests"
                    }
                    placement="top"
                  >
                    <span>
                      <Button
                        onClick={async () => {
                          await handleDownload(version);
                        }}
                        disabled={version.status !== "succeeded"}
                        data-testid={`download-button-${version.version.replace(/\./g, "")}`}
                      >
                        <CustomIcon
                          name={Icons.DOWNLOAD_SIMPLE}
                          fill={version.status === "succeeded" ? "#d04a02" : "#ccc"}
                          width={"2em"}
                          height={"2em"}
                        />
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            );
          })
        : (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            py={8}
            textAlign="center"
          >
            <CustomIcon
              name={Icons.DATABASE}
              width="80px"
              height="80px"
              fill="#ccc"
            />
            <Typography variant="h4" sx={{ color: "#6A6A6A", mt: 3, mb: 2 }}>
              No data ingests yet
            </Typography>
            <Typography variant="body1" sx={{ color: "#6A6A6A" }}>
              Upload your first dataset to start tracking version history
            </Typography>
          </Box>
        )}
    </Box>
  );
}

export default VersionHistory;
